import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, ImagePlus, Trash2 } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { deletePage } from '../../lib/admin-pin-client';
import { pageDeletedToast, pushScoringToast } from '../common/admin-toasts';
import { usePinDelete } from '../common/PinDeleteDialog';
import { apiUrl } from '../../lib/api-base';
import { prepareContentPhotoFile } from '../../lib/event-flyer-compress';
import { pageImageIdFromUrl, type PageBlock } from '../../lib/page-blocks';
import { MarkdownContent } from '../markdown/MarkdownContent';
import { useApiJson } from '../../hooks/useApi';

interface PageRecord {
  id: string;
  slug: string;
  title: string;
  description: string;
  hero: { url: string | null; width: number | null; height: number | null } | null;
  blocks: PageBlock[];
  publishState: 'draft' | 'published';
  showInNav: boolean;
  navLabel: string | null;
  navOrder: number;
}

const inputClass =
  'w-full min-h-11 bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2.5 px-3.5 text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)] text-sm';

const BLOCK_TYPES: { type: PageBlock['type']; label: string }[] = [
  { type: 'heading', label: 'Heading' },
  { type: 'text', label: 'Text' },
  { type: 'image', label: 'Image' },
  { type: 'button', label: 'Button' },
  { type: 'gallery', label: 'Gallery' },
  { type: 'divider', label: 'Divider' },
];

function emptyBlock(type: PageBlock['type']): PageBlock {
  switch (type) {
    case 'heading':
      return { type, text: '' };
    case 'text':
      return { type, markdown: '' };
    case 'image':
      return { type, url: '', alt: '', caption: null, width: null, height: null };
    case 'button':
      return { type, label: '', url: '', style: 'primary' };
    case 'gallery':
      return { type, albumSlug: '' };
    case 'divider':
      return { type: 'divider' };
  }
}

function mediaSrc(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return apiUrl(url);
}

function PageImage({ url, alt, className }: { url: string; alt: string; className: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const imageId = pageImageIdFromUrl(url);
    if (!imageId) {
      setSrc(mediaSrc(url));
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    fetch(apiUrl(`/api/pages/images/${imageId}`), { credentials: 'include' })
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error('missing'))))
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) URL.revokeObjectURL(objectUrl);
        else setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  if (!src) return <div className={className} />;
  return <img src={src} alt={alt} className={className} />;
}

export function PagesManagement() {
  const { data, mutate } = useApiJson<{ pages: PageRecord[] }>('/api/pages');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const pinDelete = usePinDelete();
  const pages = (data?.pages ?? []).filter((page) => !removedIds.has(page.id));
  const selected = pages.find((page) => page.id === selectedId) ?? null;

  return (
    <div className="space-y-4">
      {pinDelete.dialog}
      <div className="flex justify-end">
        <button
          type="button"
          className="min-h-11 rounded-[var(--radius-md)] bg-[var(--color-accent)] px-4 text-sm font-medium text-white"
          onClick={() => {
            setCreating(true);
            setSelectedId(null);
          }}
        >
          New page
        </button>
      </div>
      {error && <p className="text-sm text-[var(--color-error-text)]">{error}</p>}
      {creating && (
        <form
          className="space-y-3 rounded-[var(--radius-md)] border border-[var(--color-border)] p-4"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            try {
              const res = await apiFetch<{ page: PageRecord }>('/api/pages', {
                method: 'POST',
                body: JSON.stringify({ title: String(form.get('title') ?? ''), slug: String(form.get('slug') ?? '') }),
              });
              await mutate();
              setCreating(false);
              setSelectedId(res.page.id);
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Could not create the page');
            }
          }}
        >
          <h3 className="text-base font-semibold">New page</h3>
          <input name="title" required placeholder="Title" className={inputClass} />
          <input name="slug" placeholder="slug (optional)" className={inputClass} />
          <div className="flex gap-2">
            <button type="button" className="min-h-11 flex-1 rounded-[var(--radius-md)] border border-[var(--color-border)] text-sm" onClick={() => setCreating(false)}>
              Cancel
            </button>
            <button type="submit" className="min-h-11 flex-1 rounded-[var(--radius-md)] bg-[var(--color-accent)] text-sm font-medium text-white">
              Create page
            </button>
          </div>
        </form>
      )}
      {!selected && !creating && (
        <ul className="divide-y divide-[var(--color-border)] rounded-[var(--radius-md)] border border-[var(--color-border)]">
          {pages.length === 0 && <li className="px-4 py-8 text-sm text-[var(--color-text-secondary)]">No pages yet.</li>}
          {pages.map((page) => (
            <li key={page.id}>
              <button type="button" className="flex min-h-11 w-full items-center justify-between px-4 py-3 text-left hover:bg-[var(--color-surface-2)]" onClick={() => setSelectedId(page.id)}>
                <span>
                  <span className="block text-sm font-medium">{page.title}</span>
                  <span className="text-xs text-[var(--color-text-tertiary)]">/p/{page.slug}</span>
                </span>
                <span className="text-xs capitalize text-[var(--color-text-secondary)]">{page.publishState}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {selected && (
        <PageEditor
          key={selected.id}
          page={selected}
          onBack={() => setSelectedId(null)}
          onChange={mutate}
          onError={setError}
          askDelete={pinDelete.ask}
          onDeleted={() => {
            setRemovedIds((prev) => new Set(prev).add(selected.id));
            setSelectedId(null);
          }}
        />
      )}
    </div>
  );
}

function PageEditor({
  page,
  onBack,
  onChange,
  onError,
  askDelete,
  onDeleted,
}: {
  page: PageRecord;
  onBack: () => void;
  onChange: () => Promise<void>;
  onError: (message: string) => void;
  askDelete: ReturnType<typeof usePinDelete>['ask'];
  onDeleted: () => void;
}) {
  const [title, setTitle] = useState(page.title);
  const [slug, setSlug] = useState(page.slug);
  const [description, setDescription] = useState(page.description);
  const [publishState, setPublishState] = useState(page.publishState);
  const [showInNav, setShowInNav] = useState(page.showInNav);
  const [navLabel, setNavLabel] = useState(page.navLabel ?? '');
  const [navOrder, setNavOrder] = useState(String(page.navOrder));
  const [blocks, setBlocks] = useState<PageBlock[]>(page.blocks);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cover, setCover] = useState(page.hero);
  const [coverBusy, setCoverBusy] = useState(false);

  const save = async (nextBlocks = blocks) => {
    setSaving(true);
    try {
      await apiFetch(`/api/pages/${page.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title,
          slug,
          description,
          publishState,
          showInNav,
          navLabel,
          navOrder: Number(navOrder) || 0,
          blocks: nextBlocks,
        }),
      });
      await onChange();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save the page');
    } finally {
      setSaving(false);
    }
  };

  const uploadCover = async (file: File) => {
    setCoverBusy(true);
    try {
      const ready = await prepareContentPhotoFile(file);
      const form = new FormData();
      form.append('image', ready);
      const res = await apiFetch<{ image: { url: string; width: number | null; height: number | null } }>('/api/pages/images', {
        method: 'POST',
        body: form,
      });
      await apiFetch(`/api/pages/${page.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ heroUrl: res.image.url, heroWidth: res.image.width, heroHeight: res.image.height }),
      });
      setCover({ url: res.image.url, width: res.image.width, height: res.image.height });
      await onChange();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not upload the cover');
    } finally {
      setCoverBusy(false);
    }
  };

  const removeCover = async () => {
    setCoverBusy(true);
    try {
      await apiFetch(`/api/pages/${page.id}`, { method: 'PATCH', body: JSON.stringify({ heroUrl: null }) });
      setCover(null);
      await onChange();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not remove the cover');
    } finally {
      setCoverBusy(false);
    }
  };

  const uploadImage = async (file: File, index: number) => {
    try {
      const ready = await prepareContentPhotoFile(file);
      const form = new FormData();
      form.append('image', ready);
      const res = await apiFetch<{ image: { url: string; width: number | null; height: number | null } }>('/api/pages/images', {
        method: 'POST',
        body: form,
      });
      setBlocks((prev) =>
        prev.map((block, i) =>
          i === index && block.type === 'image'
            ? { ...block, url: res.image.url, width: res.image.width, height: res.image.height }
            : block,
        ),
      );
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not upload the image');
    }
  };

  return (
    <div className="space-y-4">
      <button type="button" className="min-h-11 text-sm text-[var(--color-accent)]" onClick={onBack}>
        All pages
      </button>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="text-xs font-medium text-[var(--color-text-secondary)]">
          Title
          <input className={`${inputClass} mt-1`} value={title} onChange={(ev) => setTitle(ev.target.value)} />
        </label>
        <label className="text-xs font-medium text-[var(--color-text-secondary)]">
          Slug
          <input className={`${inputClass} mt-1`} value={slug} onChange={(ev) => setSlug(ev.target.value)} />
        </label>
        <label className="text-xs font-medium text-[var(--color-text-secondary)] md:col-span-2">
          Description
          <input className={`${inputClass} mt-1`} value={description} onChange={(ev) => setDescription(ev.target.value)} />
        </label>
        <label className="text-xs font-medium text-[var(--color-text-secondary)]">
          Status
          <select className={`${inputClass} mt-1`} value={publishState} onChange={(ev) => setPublishState(ev.target.value as 'draft' | 'published')}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </label>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input type="checkbox" checked={showInNav} onChange={(ev) => setShowInNav(ev.target.checked)} />
          Show in menu
        </label>
        <label className="text-xs font-medium text-[var(--color-text-secondary)]">
          Menu label
          <input className={`${inputClass} mt-1`} value={navLabel} onChange={(ev) => setNavLabel(ev.target.value)} />
        </label>
        <label className="text-xs font-medium text-[var(--color-text-secondary)]">
          Menu order
          <input className={`${inputClass} mt-1`} value={navOrder} onChange={(ev) => setNavOrder(ev.target.value)} inputMode="numeric" />
        </label>
        <div className="md:col-span-2 space-y-2">
          <span className="block text-xs font-medium text-[var(--color-text-secondary)]">Cover image</span>
          {cover?.url ? (
            <PageImage url={cover.url} alt="" className="max-h-40 w-full rounded-[var(--radius-md)] object-contain bg-[var(--color-surface-2)]" />
          ) : (
            <p className="text-sm text-[var(--color-text-tertiary)]">No cover image yet.</p>
          )}
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] px-3 text-sm">
              <ImagePlus className="h-4 w-4" />
              {coverBusy ? 'Working…' : cover?.url ? 'Replace cover' : 'Add cover'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="sr-only"
                disabled={coverBusy}
                onChange={(ev) => {
                  const file = ev.target.files?.[0];
                  ev.target.value = '';
                  if (file) void uploadCover(file);
                }}
              />
            </label>
            {cover?.url && (
              <button type="button" disabled={coverBusy} className="min-h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] px-3 text-sm" onClick={() => void removeCover()}>
                Remove cover
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={saving} onClick={() => void save()} className="min-h-11 rounded-[var(--radius-md)] bg-[var(--color-accent)] px-4 text-sm font-medium text-white disabled:opacity-50">
          Save page
        </button>
        <button type="button" className="min-h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] px-4 text-sm" onClick={() => setPreview((value) => !value)}>
          {preview ? 'Edit blocks' : 'Preview'}
        </button>
        <button
          type="button"
          className="min-h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] px-4 text-sm"
          onClick={() => {
            askDelete({
              title: `Delete the page “${page.title}”?`,
              description: 'This cannot be undone.',
              confirmLabel: 'Delete page',
              run: async (pin) => {
                await deletePage(page.id, pin);
                pushScoringToast(pageDeletedToast(page.title));
                onDeleted();
                void onChange();
              },
            });
          }}
        >
          Delete page
        </button>
      </div>
      {preview ? (
        <article className="space-y-4 rounded-[var(--radius-md)] border border-[var(--color-border)] p-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          {blocks.map((block, index) => (
            <BlockPreview key={index} block={block} />
          ))}
        </article>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {BLOCK_TYPES.map((item) => (
              <button
                key={item.type}
                type="button"
                className="min-h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] px-3 text-sm"
                onClick={() => setBlocks((prev) => [...prev, emptyBlock(item.type)])}
              >
                Add {item.label}
              </button>
            ))}
          </div>
          {blocks.map((block, index) => (
            <div key={index} className="space-y-2 rounded-[var(--radius-md)] border border-[var(--color-border)] p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]">{block.type}</span>
                <div className="flex gap-1">
                  <button type="button" aria-label="Move block up" disabled={index === 0} className="min-h-11 min-w-11 rounded-[var(--radius-md)] border border-[var(--color-border)]" onClick={() => setBlocks((prev) => move(prev, index, -1))}>
                    <ChevronUp className="mx-auto h-4 w-4" />
                  </button>
                  <button type="button" aria-label="Move block down" disabled={index === blocks.length - 1} className="min-h-11 min-w-11 rounded-[var(--radius-md)] border border-[var(--color-border)]" onClick={() => setBlocks((prev) => move(prev, index, 1))}>
                    <ChevronDown className="mx-auto h-4 w-4" />
                  </button>
                  <button type="button" aria-label="Remove block" className="min-h-11 min-w-11 rounded-[var(--radius-md)] border border-[var(--color-border)]" onClick={() => setBlocks((prev) => prev.filter((_, i) => i !== index))}>
                    <Trash2 className="mx-auto h-4 w-4" />
                  </button>
                </div>
              </div>
              <BlockFields block={block} onChange={(next) => setBlocks((prev) => prev.map((item, i) => (i === index ? next : item)))} onUpload={(file) => void uploadImage(file, index)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function move(blocks: PageBlock[], index: number, delta: number): PageBlock[] {
  const next = [...blocks];
  const target = index + delta;
  const current = next[index];
  const swap = next[target];
  if (!current || !swap) return blocks;
  next[index] = swap;
  next[target] = current;
  return next;
}

function BlockFields({
  block,
  onChange,
  onUpload,
}: {
  block: PageBlock;
  onChange: (block: PageBlock) => void;
  onUpload: (file: File) => void;
}) {
  if (block.type === 'heading') {
    return <input className={inputClass} value={block.text} placeholder="Heading" onChange={(ev) => onChange({ ...block, text: ev.target.value })} />;
  }
  if (block.type === 'text') {
    return <textarea className={inputClass} rows={5} value={block.markdown} placeholder="Markdown" onChange={(ev) => onChange({ ...block, markdown: ev.target.value })} />;
  }
  if (block.type === 'image') {
    return (
      <div className="space-y-2">
        {block.url && <PageImage url={block.url} alt="" className="max-h-48 w-full rounded-[var(--radius-md)] object-contain" />}
        <label className="inline-flex min-h-11 cursor-pointer items-center rounded-[var(--radius-md)] border border-[var(--color-border)] px-3 text-sm">
          Upload image
          <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(ev) => {
            const file = ev.target.files?.[0];
            ev.target.value = '';
            if (file) onUpload(file);
          }} />
        </label>
        <input className={inputClass} value={block.alt} placeholder="Alt text" onChange={(ev) => onChange({ ...block, alt: ev.target.value })} />
        <input className={inputClass} value={block.caption ?? ''} placeholder="Caption" onChange={(ev) => onChange({ ...block, caption: ev.target.value || null })} />
      </div>
    );
  }
  if (block.type === 'button') {
    return (
      <div className="grid gap-2 md:grid-cols-3">
        <input className={inputClass} value={block.label} placeholder="Label" onChange={(ev) => onChange({ ...block, label: ev.target.value })} />
        <input className={inputClass} value={block.url} placeholder="https://, /path, mailto:, or tel:" onChange={(ev) => onChange({ ...block, url: ev.target.value })} />
        <select className={inputClass} value={block.style} onChange={(ev) => onChange({ ...block, style: ev.target.value === 'secondary' ? 'secondary' : 'primary' })}>
          <option value="primary">Primary</option>
          <option value="secondary">Secondary</option>
        </select>
      </div>
    );
  }
  if (block.type === 'gallery') {
    return <input className={inputClass} value={block.albumSlug} placeholder="Album slug" onChange={(ev) => onChange({ ...block, albumSlug: ev.target.value })} />;
  }
  return <p className="text-sm text-[var(--color-text-tertiary)]">A horizontal divider.</p>;
}

function BlockPreview({ block }: { block: PageBlock }) {
  if (block.type === 'heading') return <h3 className="text-lg font-semibold">{block.text}</h3>;
  if (block.type === 'text') return <MarkdownContent text={block.markdown} />;
  if (block.type === 'image' && block.url) {
    return (
      <figure>
        <PageImage url={block.url} alt={block.alt} className="max-h-80 w-full rounded-[var(--radius-md)] object-contain" />
        {block.caption && <figcaption className="mt-1 text-xs text-[var(--color-text-tertiary)]">{block.caption}</figcaption>}
      </figure>
    );
  }
  if (block.type === 'button') {
    return (
      <p>
        <span className={block.style === 'secondary' ? 'inline-flex min-h-11 items-center rounded-[var(--radius-md)] border border-[var(--color-border)] px-4 text-sm' : 'inline-flex min-h-11 items-center rounded-[var(--radius-md)] bg-[var(--color-accent)] px-4 text-sm text-white'}>
          {block.label}
        </span>
      </p>
    );
  }
  if (block.type === 'gallery') return <p className="text-sm text-[var(--color-text-secondary)]">Gallery: {block.albumSlug}</p>;
  if (block.type === 'divider') return <hr className="border-[var(--color-border)]" />;
  return null;
}
